import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Configure axios instance for redactor service
const redactorClient = axios.create({
  baseURL: config.api.redactorServiceUrl,
  timeout: 600000, // 10 minutes – redaction can be slow for large files
  headers: {},
});

// Add auth token to every request
redactorClient.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('token');
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(redactorClient);

applyTokenRefreshInterceptor(redactorClient);

const redactorService = {
  /**
   * Upload files to create a new redaction job.
   * @param {File[]} files - Array of File objects (max 20).
   * @returns {Promise<{job_id: string, files: Array}>}
   */
  uploadFiles: async (files) => {
    const DIRECT_UPLOAD_THRESHOLD = 3 * 1024 * 1024;
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);

    if (totalSize > DIRECT_UPLOAD_THRESHOLD) {
      try {
        const { data: reservation } = await redactorClient.post('/redactor/upload/presigned', {
          files: files.map((file) => ({
            filename: file.name,
            size: file.size,
            content_type: file.type || 'application/octet-stream',
          })),
        });

        await Promise.all(
          reservation.files.map((reserved, index) =>
            axios.put(reserved.upload_url, files[index], {
              headers: { 'Content-Type': reserved.content_type },
              transformRequest: [(data) => data],
            })
          )
        );

        return {
          job_id: reservation.job_id,
          files: reservation.files.map(({ file_id, original_filename }) => ({
            file_id,
            original_filename,
          })),
        };
      } catch (err) {
        const statusCode = err?.response?.status;
        if (statusCode !== 404 && statusCode !== 503) {
          throw err;
        }
        // Presigned uploads unavailable (S3 disabled); fall through to multipart upload
      }
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await redactorClient.post('/redactor/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Start the redaction process with selected categories.
   * @param {string} jobId
   * @param {string[]} categories - Array of RedactionType values.
   * @returns {Promise<Object>} JobResponse
   */
  startRedaction: async (jobId, categories, useOcr = true) => {
    const response = await redactorClient.post('/redactor/start', {
      job_id: jobId,
      settings: { categories, use_ocr: useOcr },
    });
    return response.data;
  },

  /**
   * Poll job status / results.
   * @param {string} jobId
   * @returns {Promise<Object>} JobResponse
   */
  getJobStatus: async (jobId) => {
    const response = await redactorClient.get(`/redactor/job/${jobId}`);
    return response.data;
  },

  /**
   * Resolve a redactor file response that may be a blob or a presigned URL.
   * Large files return JSON `{download_url}` so Lambda never streams them.
   * @param {import('axios').AxiosResponse} response
   * @returns {Promise<Blob>}
   */
  _blobFromFileResponse: async (response) => {
    const data = response.data;
    if (data instanceof Blob) {
      const contentType = (data.type || response.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const parsed = JSON.parse(await data.text());
        if (parsed.download_url) {
          const fileResponse = await fetch(parsed.download_url);
          if (!fileResponse.ok) {
            throw new Error(`Failed to download file: ${fileResponse.status}`);
          }
          return fileResponse.blob();
        }
      }
      return data;
    }
    if (data && typeof data === 'object' && data.download_url) {
      const fileResponse = await fetch(data.download_url);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      return fileResponse.blob();
    }
    throw new Error('Redactor did not return file content or a download URL.');
  },

  /**
   * Trigger a browser download for a blob.
   * @param {Blob} blob
   * @param {string} filename
   */
  _saveBlob: (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Get a preview URL for a file (original or redacted).
   * Returns a blob URL for rendering in an iframe / embed.
   * @param {string} jobId
   * @param {string} fileId
   * @param {string} version - 'original' or 'redacted'
   * @returns {Promise<string>} Blob URL
   */
  getFilePreviewUrl: async (jobId, fileId, version = 'redacted') => {
    const response = await redactorClient.get(
      `/redactor/job/${jobId}/file/${fileId}/preview`,
      {
        params: { version },
        responseType: 'blob',
      }
    );
    const blob = await redactorService._blobFromFileResponse(response);
    return URL.createObjectURL(blob);
  },

  /**
   * Download a single file.
   * @param {string} jobId
   * @param {string} fileId
   * @param {string} version
   * @param {string} filename - Suggested download filename.
   */
  downloadFile: async (jobId, fileId, version = 'redacted', filename) => {
    const response = await redactorClient.get(
      `/redactor/job/${jobId}/file/${fileId}/download`,
      {
        params: { version },
        responseType: 'blob',
      }
    );
    const blob = await redactorService._blobFromFileResponse(response);
    redactorService._saveBlob(blob, filename || 'download');
  },

  /**
   * Download all redacted files as a ZIP, or each file when Lambda
   * returns presigned URLs instead of an archive.
   * @param {string} jobId
   */
  downloadAll: async (jobId) => {
    const response = await redactorClient.get(
      `/redactor/job/${jobId}/download-all`,
      { responseType: 'blob' }
    );
    const data = response.data;
    if (data instanceof Blob) {
      const contentType = (data.type || response.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const parsed = JSON.parse(await data.text());
        if (Array.isArray(parsed.files)) {
          await Promise.all(
            parsed.files.map(async (file) => {
              const fileResponse = await fetch(file.download_url);
              if (!fileResponse.ok) {
                throw new Error(`Failed to download file: ${fileResponse.status}`);
              }
              const blob = await fileResponse.blob();
              redactorService._saveBlob(blob, file.filename || 'download');
            })
          );
          return;
        }
      }
      redactorService._saveBlob(data, `redacted_${jobId.slice(0, 8)}.zip`);
      return;
    }
    if (data && Array.isArray(data.files)) {
      await Promise.all(
        data.files.map(async (file) => {
          const fileResponse = await fetch(file.download_url);
          if (!fileResponse.ok) {
            throw new Error(`Failed to download file: ${fileResponse.status}`);
          }
          const blob = await fileResponse.blob();
          redactorService._saveBlob(blob, file.filename || 'download');
        })
      );
      return;
    }
    throw new Error('Redactor did not return a ZIP or download URLs.');
  },

  /**
   * Manually add or remove a redaction.
   * @param {string} jobId
   * @param {string} fileId
   * @param {Object} edit - ManualRedactionEdit payload.
   * @returns {Promise<Object>}
   */
  editRedaction: async (jobId, fileId, edit) => {
    const response = await redactorClient.post(
      `/redactor/job/${jobId}/file/${fileId}/edit`,
      edit
    );
    return response.data;
  },

  /**
   * Fetch the raw text content of a txt file for in-component rendering.
   * @param {string} jobId
   * @param {string} fileId
   * @param {string} version - 'original' or 'redacted'
   * @returns {Promise<string>} Plain text content
   */
  getFileTxtContent: async (jobId, fileId, version = 'redacted') => {
    const response = await redactorClient.get(
      `/redactor/job/${jobId}/file/${fileId}/preview`,
      { params: { version }, responseType: 'blob' }
    );
    const blob = await redactorService._blobFromFileResponse(response);
    return blob.text();
  },

  /**
   * Get a rendered PNG image of a single PDF page.
   * Used for manual-redact draw mode so coordinates map exactly.
   * @param {string} jobId
   * @param {string} fileId
   * @param {string} version - 'original' or 'redacted'
   * @param {number} page - 1-based page number
   * @returns {Promise<string>} Blob URL or presigned URL for the PNG image
   */
  getPageImageUrl: async (jobId, fileId, version = 'redacted', page = 1) => {
    const response = await redactorClient.get(
      `/redactor/job/${jobId}/file/${fileId}/page-image`,
      {
        params: { version, page },
        responseType: 'blob',
      }
    );
    const data = response.data;
    if (data instanceof Blob) {
      const contentType = (data.type || response.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const parsed = JSON.parse(await data.text());
        if (parsed.download_url) {
          return parsed.download_url;
        }
      }
      return URL.createObjectURL(data);
    }
    if (data && typeof data === 'object' && data.download_url) {
      return data.download_url;
    }
    throw new Error('Redactor did not return a page image or download URL.');
  },

  /**
   * Rotate all pages of a PDF file (both original and redacted).
   * @param {string} jobId
   * @param {string} fileId
   * @param {number} degrees - 90 (clockwise), -90 (counter-clockwise), or 180
   * @returns {Promise<Object>}
   */
  rotatePdf: async (jobId, fileId, degrees) => {
    const response = await redactorClient.post(
      `/redactor/job/${jobId}/file/${fileId}/rotate`,
      { degrees }
    );
    return response.data;
  },
};

export default redactorService;
